import React, { useState, useEffect } from "react";
import {
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import PermissionTable from "./Components/PermissionTable";
import permissionGroups from "./Utils/permissionGroups";
import { ArrowLeft, RotateCcw, Info, Search, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Permissions } from "../../../permissions";

const CreateNewRole = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const initialPermissionsState = {};

  const [errors, setErrors] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // Initialize all permissions as unselected
  useEffect(() => {
    const initialPermissions = {};
    Permissions.forEach(permission => {
      initialPermissions[permission] = false;
    });
    setSelectedPermissions(initialPermissions);
    // Store the initial state for reset functionality
    Object.assign(initialPermissionsState, initialPermissions);
  }, []);

  // Handle input changes
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Handle permission toggle
  const handlePermissionChange = permission => {
    setSelectedPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  };

  // Handle group toggle
  const handleGroupChange = updates => {
    setSelectedPermissions(prev => ({
      ...prev,
      ...updates,
    }));
  };

  // Reset form to initial state
  const handleResetForm = () => {
    setFormData({
      name: "",
      description: "",
    });

    // Reset all permissions to unchecked
    const resetPermissions = {};
    Permissions.forEach(permission => {
      resetPermissions[permission] = false;
    });
    setSelectedPermissions(resetPermissions);

    // Clear any errors
    setErrors({});
    setSaveError(null);

    // Reset search and filters
    setSearchQuery("");
    setShowSelectedOnly(false);
  };

  // Categories for filtering permissions
  const categories = [
    { id: "all", name: "All Permissions" },
    { id: "dashboard", name: "Dashboard" },
    { id: "products", name: "Products" },
    { id: "customers", name: "Customers" },
    { id: "orders", name: "Orders" },
    { id: "receipts", name: "Receipts" },
    { id: "vouchers", name: "Vouchers" },
    { id: "reports", name: "Reports" },
    { id: "usermanagement", name: "User Management" },
    { id: "finance", name: "Finance" },
    { id: "loyalty", name: "Loyalty" },
  ];

  // Get filtered permission groups based on selected category, search, and selected-only filter
  const getFilteredPermissionGroups = () => {
    let filteredGroups = [...permissionGroups];

    // Filter by category
    if (selectedCategory !== "all") {
      // Map category id to related module names
      const categoryToModules = {
        dashboard: ["Dashboard"],
        products: ["Products", "Units", "Categories", "Brands"],
        customers: [
          "Customers",
          "Customer Billing",
          "Customer Requests",
          "Locations",
        ],
        orders: ["Orders"],
        receipts: ["Receipts"],
        vouchers: ["Common Vouchers", "User Vouchers", "Posters"],
        reports: ["Outstanding Balances", "Ledger", "GST Reports"],
        usermanagement: ["Employees", "Roles"],
        finance: ["Finance"],
        loyalty: ["Loyalty"],
        settings: ["Settings"],
      };

      const relevantModules = categoryToModules[selectedCategory] || [];
      filteredGroups = filteredGroups.filter(group =>
        relevantModules.includes(group.module),
      );
    }

    // Search functionality
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      // Filter groups and their permissions
      filteredGroups = filteredGroups
        .map(group => {
          // Check if group name matches
          const groupMatches = group.module.toLowerCase().includes(query);

          // Filter permissions that match the query
          const matchingPermissions = group.permissions.filter(permission => {
            return (
              permission.toLowerCase().includes(query) ||
              getHumanReadablePermission(permission)
                .toLowerCase()
                .includes(query)
            );
          });

          // Return the group with filtered permissions if there are matches
          // or if the group name itself matches
          if (matchingPermissions.length > 0 || groupMatches) {
            return {
              ...group,
              permissions:
                matchingPermissions.length > 0
                  ? matchingPermissions
                  : group.permissions,
            };
          }
          return null;
        })
        .filter(Boolean); // Remove null groups (no matches)
    }

    // Show only selected permissions
    if (showSelectedOnly) {
      filteredGroups = filteredGroups
        .map(group => {
          const selectedGroupPermissions = group.permissions.filter(
            permission => selectedPermissions[permission],
          );

          if (selectedGroupPermissions.length > 0) {
            return {
              ...group,
              permissions: selectedGroupPermissions,
            };
          }
          return null;
        })
        .filter(Boolean); // Remove null groups (no selected permissions)
    }

    return filteredGroups;
  };

  // Convert permission string to human-readable format
  const getHumanReadablePermission = permission => {
    if (!permission) return "";

    const parts = permission.split("_");
    if (parts.length < 2) return permission;

    const action = parts[0];
    const resource = parts.slice(1).join(" ");

    return `${action.charAt(0).toUpperCase() + action.slice(1)} ${resource}`;
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Role name is required";
    }

    // Check if at least one permission is selected
    const hasSelectedPermissions = Object.values(selectedPermissions).some(
      value => value,
    );
    if (!hasSelectedPermissions) {
      newErrors.permissions = "Please select at least one permission";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Get selected permissions as an array
      const permissionsArray = Object.entries(selectedPermissions)
        .filter(([_, isSelected]) => isSelected)
        .map(([permission]) => permission);

      // Mock API call
      console.log("Submitting role:", {
        name: formData.name,
        description: formData.description,
        permissions: permissionsArray,
      });

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Redirect to roles list on success
      navigate("/usermanagement/roles");
    } catch (error) {
      setSaveError("Failed to save role. Please try again.");
      console.error("Error saving role:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/usermanagement/roles");
  };

  // Get count of selected permissions
  const getSelectedPermissionsCount = () => {
    return Object.values(selectedPermissions).filter(Boolean).length;
  };

  return (
    <div className="bg-white h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Create New Role
          </h1>
          <div className="ml-3 text-sm text-gray-500 font-outfit">
            Define role details and permissions
          </div>
        </div>
        <button
          onClick={() => navigate("/usermanagement/roles")}
          className="flex items-center text-[#5652B0] hover:text-[#4A47A3] font-medium">
          <ArrowLeft size={18} className="mr-1" />
          Back to Roles
        </button>
      </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {saveError && (
          <Alert severity="error" className="mb-6">
            {saveError}
          </Alert>
        )}

        {/* Role details section */}
        <div className="mb-8 bg-white p-6 rounded-lg border border-[#E5E7EB] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium font-outfit text-[#374151]">
              Role Details
            </h2>
            <Tooltip title="Reset form to default values">
              <button
                onClick={handleResetForm}
                className="flex items-center text-[#5652B0] hover:text-[#4A47A3] text-sm font-medium">
                <RotateCcw size={16} className="mr-1" />
                Reset Form
              </button>
            </Tooltip>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <TextField
                id="name"
                name="name"
                label="Role Name"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                className="mb-4"
                InputProps={{
                  style: {
                    fontFamily: "Outfit",
                    fontSize: "16px",
                  },
                }}
                InputLabelProps={{
                  style: {
                    fontFamily: "Outfit",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                  "& .MuiFormLabel-root.Mui-focused": {
                    color: "#5652B0",
                  },
                }}
              />
            </div>
            <div>
              <TextField
                id="description"
                name="description"
                label="Description (Optional)"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.description}
                onChange={handleInputChange}
                className="mb-4"
                InputProps={{
                  style: {
                    fontFamily: "Outfit",
                    fontSize: "16px",
                  },
                }}
                InputLabelProps={{
                  style: {
                    fontFamily: "Outfit",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                  "& .MuiFormLabel-root.Mui-focused": {
                    color: "#5652B0",
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Permissions section */}
        <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <h2 className="text-lg font-medium font-outfit text-[#374151]">
                Permissions
              </h2>
              <div className="ml-3 bg-[rgb(86,82,176,0.1)] text-[#5652B0] text-xs font-medium px-2 py-1 rounded-full">
                {getSelectedPermissionsCount()} selected
              </div>
              <Tooltip title="Select the permissions this role will have access to">
                <div className="ml-2 cursor-help">
                  <Info size={16} className="text-gray-400" />
                </div>
              </Tooltip>
            </div>
          </div>

          {errors.permissions && (
            <Alert severity="error" className="mb-4">
              {errors.permissions}
            </Alert>
          )}

          {/* Search and filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-grow">
              <TextField
                fullWidth
                size="small"
                placeholder="Search permissions..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} className="text-gray-400" />
                    </InputAdornment>
                  ),
                  style: {
                    fontFamily: "Outfit",
                    fontSize: "14px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                }}
              />
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                className={`flex items-center px-3 py-2 rounded text-sm ${
                  showSelectedOnly
                    ? "bg-[rgb(86,82,176,0.1)] text-[#5652B0]"
                    : "text-[#4B5563] hover:bg-gray-50"
                }`}>
                <Filter size={16} className="mr-1" />
                {showSelectedOnly
                  ? "Showing selected only"
                  : "Show all permissions"}
              </button>
            </div>
          </div>

          {/* Category tabs */}
          <div className="mb-6 border-b border-[#E5E7EB]">
            <div className="flex flex-wrap -mb-px">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`px-4 py-2 text-sm font-medium transition-all border-b-2 mr-2 ${
                    selectedCategory === category.id
                      ? "text-[#5652B0] border-[#5652B0]"
                      : "text-[#4B5563] border-transparent hover:text-[#5652B0] hover:border-[rgb(86,82,176,0.3)]"
                  }`}
                  onClick={() => setSelectedCategory(category.id)}>
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Permissions table */}
          <PermissionTable
            permissionGroups={getFilteredPermissionGroups()}
            selectedPermissions={selectedPermissions}
            onPermissionChange={handlePermissionChange}
            onGroupChange={handleGroupChange}
          />

          {/* No results message */}
          {getFilteredPermissionGroups().length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No permissions found matching your criteria.
              {searchQuery && (
                <div className="mt-2">
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-[#5652B0] hover:text-[#4A47A3] text-sm font-medium">
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-4 mt-8">
          <button
            onClick={handleCancel}
            className="px-6 py-2.5 rounded-lg text-base font-medium border-2 border-[#5652B0] text-[#5652B0] hover:bg-[rgb(86,82,176,0.1)] transition-all"
            disabled={loading}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2.5 rounded-lg text-base font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all flex items-center"
            disabled={loading}>
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" className="mr-2" />
                Saving...
              </>
            ) : (
              "Save Role"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewRole;
